import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Mail, Phone, ArrowRight, Loader2, Clock, Compass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "new", label: "New", hint: "Just came in" },
  { key: "welcomed", label: "Welcomed", hint: "Greeted personally" },
  { key: "active", label: "Active", hint: "Engaging with events" },
  { key: "nurtured", label: "Nurtured", hint: "Long-term relationship" },
];

const STAGE_ORDER = STAGES.map((s) => s.key);

function displayName(c) {
  return (
    [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
    c.name ||
    c.email ||
    "Unnamed"
  );
}

// Format an ISO timestamp as a short relative time (e.g. "2h ago", "3d ago")
function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then || Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  if (diffMs < 0) return "just now";
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  const wk = Math.floor(d / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(d / 365);
  return `${yr}y ago`;
}

// Map raw history action codes to short human labels
const ACTION_LABELS = {
  created: "Signed up",
  updated: "Updated",
  self_update: "Self-updated profile",
  archived: "Archived",
  unarchived: "Restored",
  tag_added: "Tagged",
  tag_removed: "Tag removed",
  tag_renamed: "Tag renamed",
  read: "Read",
  compass_handoff: "Compass handoff",
};

function describeHistoryEntry(entry) {
  if (!entry) return "";
  const base = ACTION_LABELS[entry.action] || entry.action?.replace(/_/g, " ") || "Activity";
  // Status changes deserve extra clarity
  if (entry.action === "updated" && Array.isArray(entry.changes) && entry.changes.includes("status")) {
    return "Stage changed";
  }
  return base;
}

// Return up to N most recent history entries, newest first
function lastHistoryEntries(history, n = 2) {
  if (!Array.isArray(history) || history.length === 0) return [];
  // History is chronological (oldest first); reverse to newest first.
  return history.slice(-n).reverse();
}

export default function ContactsPipelineBoard({ token, onContactUpdated }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movingId, setMovingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [handoffId, setHandoffId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const g = { new: [], welcomed: [], active: [], nurtured: [] };
    for (const c of contacts) {
      if (c.archived) continue;
      const s = STAGE_ORDER.includes(c.status) ? c.status : "new";
      g[s].push(c);
    }
    return g;
  }, [contacts]);

  const moveContact = async (contactId, newStatus) => {
    const current = contacts.find((c) => c.id === contactId);
    if (!current || current.status === newStatus) return;

    // Optimistic update
    setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, status: newStatus } : c)));
    setMovingId(contactId);

    try {
      const res = await fetch(`${API}/admin/contacts/${contactId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`${displayName(current)} → ${STAGES.find((s) => s.key === newStatus).label}`);
      if (onContactUpdated) onContactUpdated();
    } catch {
      // Rollback
      setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, status: current.status } : c)));
      toast.error("Move failed. Try again.");
    } finally {
      setMovingId(null);
    }
  };

  const sendToCompass = async (contact) => {
    setHandoffId(contact.id);
    try {
      const res = await fetch(`${API}/admin/contacts/${contact.id}/compass-handoff`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Handoff failed");
      const data = await res.json();
      // Open the deep-link in a new tab
      if (data.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
      // Optimistically update the contact's history so timeline reflects the sync
      const now = new Date().toISOString();
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contact.id
            ? {
                ...c,
                last_compass_handoff_at: now,
                compass_handoff_count: (c.compass_handoff_count || 0) + 1,
                history: [...(c.history || []), { action: "compass_handoff", timestamp: now, by: "admin", note: "AMS Compass trial link opened" }],
              }
            : c
        )
      );
      toast.success(`Compass trial link opened for ${displayName(contact)}`);
      if (onContactUpdated) onContactUpdated();
    } catch {
      toast.error("Couldn't start Compass handoff");
    } finally {
      setHandoffId(null);
    }
  };

  const onDragStart = (e, contactId) => {
    e.dataTransfer.setData("text/plain", contactId);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };

  const onDragLeave = () => setDragOverStage(null);

  const onDrop = (e, stageKey) => {
    e.preventDefault();
    const contactId = e.dataTransfer.getData("text/plain");
    setDragOverStage(null);
    if (contactId) moveContact(contactId, stageKey);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="pipeline-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="contacts-pipeline-board" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {STAGES.map((s) => (
          <div key={s.key} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              {s.label}
            </div>
            <div className="mt-1 text-3xl font-editorial text-primary" data-testid={`pipeline-count-${s.key}`}>
              {grouped[s.key].length}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAGES.map((stage) => {
          const items = grouped[stage.key];
          const isOver = dragOverStage === stage.key;
          return (
            <div
              key={stage.key}
              onDragOver={(e) => onDragOver(e, stage.key)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, stage.key)}
              className={`rounded-2xl border transition-colors ${
                isOver
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 bg-gray-50/60"
              } min-h-[300px] p-3`}
              data-testid={`pipeline-column-${stage.key}`}
            >
              <div className="px-2 py-2 mb-2 flex items-center justify-between">
                <div>
                  <h3 className="font-editorial text-xl leading-none">{stage.label}</h3>
                  <p className="text-xs text-gray-500 mt-1">{stage.hint}</p>
                </div>
                <span className="text-xs bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-600">
                  {items.length}
                </span>
              </div>

              <div className="space-y-2">
                {items.length === 0 && (
                  <div className="text-xs text-gray-400 italic text-center py-6 border border-dashed border-gray-200 rounded-lg">
                    Drop contacts here
                  </div>
                )}
                {items.map((c) => (
                  <PipelineCard
                    key={c.id}
                    contact={c}
                    onDragStart={onDragStart}
                    moving={movingId === c.id}
                    onQuickMove={(next) => moveContact(c.id, next)}
                    onSendCompass={() => sendToCompass(c)}
                    handoffPending={handoffId === c.id}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PipelineCard({ contact, onDragStart, moving, onQuickMove, onSendCompass, handoffPending }) {
  const currentIdx = STAGE_ORDER.indexOf(contact.status || "new");
  const nextStage = STAGE_ORDER[currentIdx + 1];
  const recent = lastHistoryEntries(contact.history, 2);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, contact.id)}
      className={`bg-white rounded-xl border border-gray-200 p-3 cursor-grab active:cursor-grabbing hover:border-primary/40 hover:shadow-sm transition-all ${
        moving ? "opacity-60" : ""
      }`}
      data-testid={`pipeline-card-${contact.id}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-body font-semibold text-sm truncate">
            {displayName(contact)}
          </p>
          {contact.email && (
            <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
              <Mail className="w-3 h-3" />
              {contact.email}
            </p>
          )}
          {contact.phone && (
            <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
              <Phone className="w-3 h-3" />
              {contact.phone}
            </p>
          )}
          {contact.event_context && (
            <span className="inline-block mt-2 text-[10px] uppercase tracking-wide bg-secondary/10 text-secondary px-1.5 py-0.5 rounded font-semibold">
              via {contact.event_context}
            </span>
          )}
          {Array.isArray(contact.tags) && contact.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {contact.tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                  {t}
                </span>
              ))}
              {contact.tags.length > 3 && (
                <span className="text-[10px] text-gray-400">+{contact.tags.length - 3}</span>
              )}
            </div>
          )}

          {/* Mini activity timeline — last 2 history entries */}
          {recent.length > 0 && (
            <div
              className="mt-2.5 pt-2 border-t border-dashed border-gray-200 space-y-1"
              data-testid={`pipeline-timeline-${contact.id}`}
            >
              {recent.map((entry, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 text-[10px] text-gray-500"
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      i === 0 ? "bg-primary" : "bg-gray-300"
                    }`}
                  />
                  <span className="truncate font-medium text-gray-600">
                    {describeHistoryEntry(entry)}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-0.5 text-gray-400 flex-shrink-0">
                    <Clock className="w-2.5 h-2.5" />
                    {relativeTime(entry.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {nextStage && (
        <button
          onClick={() => onQuickMove(nextStage)}
          className="mt-2 w-full text-[11px] inline-flex items-center justify-center gap-1 py-1 rounded-md border border-gray-200 hover:border-primary hover:text-primary transition-colors text-gray-500"
          data-testid={`pipeline-advance-${contact.id}`}
        >
          Move to {STAGES.find((s) => s.key === nextStage).label}
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
      {onSendCompass && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSendCompass();
          }}
          disabled={handoffPending}
          className="mt-1.5 w-full text-[11px] inline-flex items-center justify-center gap-1 py-1 rounded-md bg-secondary/10 text-secondary hover:bg-secondary hover:text-white transition-colors disabled:opacity-60"
          data-testid={`compass-handoff-${contact.id}`}
          title={
            contact.last_compass_handoff_at
              ? `Sent to Compass ${relativeTime(contact.last_compass_handoff_at)}`
              : "Open the Compass trial signup for this contact"
          }
        >
          {handoffPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Compass className="w-3 h-3" />
          )}
          {contact.compass_handoff_count > 0
            ? `Compass · sent ${relativeTime(contact.last_compass_handoff_at)}`
            : "Send to Compass"}
        </button>
      )}
    </div>
  );
}
